import { useState, useRef } from 'react'

const MapSearch = ({
  search,
  setSearch,
  filterAvail,
  setFilterAvail,
  count,
  availableCount,
  onLocationSearch
}) => {
  const [geocoding, setGeocoding] = useState(false)
  const [foundLocation, setFoundLocation] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const inputRef = useRef(null)

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      await handleLocationSearch()
    }
  }

  const handleLocationSearch = async () => {
    const query = inputRef.current?.value?.trim()
    if (!query) return

    setGeocoding(true)
    setFoundLocation(null)
    setNotFound(false)

    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=nl&addressdetails=1`
      const response = await fetch(url, {
        headers: {
          'Accept-Language': 'nl',
          'User-Agent': 'Buurtlader/1.0'
        }
      })
      const data = await response.json()

      if (data && data.length > 0) {
        const result = data[0]
        const lat = parseFloat(result.lat)
        const lon = parseFloat(result.lon)

        const addr = result.address || {}
        const parts = [
          addr.road,
          addr.suburb || addr.neighbourhood,
          addr.city || addr.town || addr.village || addr.municipality
        ].filter(Boolean)

        const displayName = parts.length > 0
          ? parts.join(', ')
          : result.display_name.split(',').slice(0, 2).join(',').trim()

        setFoundLocation(displayName)
        onLocationSearch(lat, lon)
      } else {
        setNotFound(true)
        setSearch(query)
      }
    } catch (error) {
      setNotFound(true)
      setSearch(query)
    } finally {
      setGeocoding(false)
    }
  }

  const handleClear = () => {
    setSearch('')
    setFoundLocation(null)
    setNotFound(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div style={{
      position: 'absolute',
      top: 0, left: 0, right: 0,
      padding: '12px 14px 10px',
      background: 'linear-gradient(180deg, rgba(247,244,238,0.98) 80%, transparent)',
      zIndex: 20,
    }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>

        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%',
            transform: 'translateY(-50%)', fontSize: 14,
            pointerEvents: 'none', zIndex: 1
          }}>
            {geocoding ? '⏳' : '🔍'}
          </span>
          <input
            ref={inputRef}
            defaultValue={search}
            onChange={e => {
              setSearch(e.target.value)
              if (!e.target.value) {
                setFoundLocation(null)
                setNotFound(false)
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder="Straat + stad, postcode of buurt..."
            style={{
              width: '100%',
              background: 'white',
              border: `1.5px solid ${notFound ? '#fca5a5' : 'var(--cream-dark)'}`,
              borderRadius: 12,
              padding: '11px 36px 11px 36px',
              color: 'var(--ink)',
              fontSize: 14,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              fontFamily: 'DM Sans, sans-serif'
            }}
            onFocus={e => {
              e.target.style.borderColor = 'var(--green)'
              e.target.style.boxShadow = '0 0 0 3px var(--green-pale)'
            }}
            onBlur={e => {
              e.target.style.borderColor = notFound ? '#fca5a5' : 'var(--cream-dark)'
              e.target.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'
            }}
          />
          {(search || foundLocation) && (
            <button
              onClick={handleClear}
              style={{
                position: 'absolute', right: 10, top: '50%',
                transform: 'translateY(-50%)',
                background: 'var(--cream-dark)', border: 'none',
                borderRadius: '50%', width: 20, height: 20,
                cursor: 'pointer', fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--ink-muted)', fontFamily: 'DM Sans, sans-serif'
              }}>
              ×
            </button>
          )}
        </div>

        <button
          onClick={handleLocationSearch}
          disabled={geocoding}
          style={{
            background: 'white', border: '1.5px solid var(--cream-dark)',
            borderRadius: 12, padding: '0 12px', color: 'var(--green)',
            fontSize: 18, cursor: geocoding ? 'wait' : 'pointer',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0
          }}
          title="Ga naar locatie">
          📍
        </button>

        <button
          onClick={() => setFilterAvail(!filterAvail)}
          style={{
            background: filterAvail ? 'var(--green-pale)' : 'white',
            border: `1.5px solid ${filterAvail ? 'var(--green)' : 'var(--cream-dark)'}`,
            borderRadius: 12, padding: '0 12px',
            color: filterAvail ? 'var(--green)' : 'var(--ink-muted)',
            fontSize: 12, fontWeight: 700, cursor: 'pointer',
            whiteSpace: 'nowrap', boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            fontFamily: 'DM Sans, sans-serif', flexShrink: 0
          }}>
          {filterAvail ? '✓' : 'Alle'}
        </button>
      </div>

      {foundLocation && (
        <div style={{
          background: 'var(--green-pale)', border: '1px solid var(--green-mid)',
          borderRadius: 8, padding: '5px 10px', fontSize: 11,
          color: 'var(--green)', fontWeight: 600, marginBottom: 6,
          display: 'flex', alignItems: 'center', gap: 5
        }}>
          <span>📍</span>
          <span>Gevonden: {foundLocation}</span>
          <span style={{ marginLeft: 'auto', color: 'var(--ink-muted)', fontWeight: 400, fontSize: 10 }}>
            Niet juist? Voeg de stad toe
          </span>
        </div>
      )}

      {notFound && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, padding: '5px 10px', fontSize: 11,
          color: '#991b1b', fontWeight: 600, marginBottom: 6,
          display: 'flex', alignItems: 'center', gap: 5
        }}>
          <span>❌</span>
          <span>Locatie niet gevonden. Probeer straat + stad toe te voegen.</span>
        </div>
      )}

      {!foundLocation && !notFound && !geocoding && (
        <div style={{ fontSize: 10, color: 'var(--ink-muted)', marginBottom: 4, paddingLeft: 4 }}>
          💡 Tip: voeg de stad toe voor betere resultaten — bijv. "Neptunusstraat Amsterdam"
        </div>
      )}

      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'white', border: '1px solid var(--cream-dark)',
        borderRadius: 100, padding: '4px 12px', fontSize: 12,
        color: 'var(--ink-muted)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <span style={{ color: 'var(--green)', fontWeight: 800 }}>{availableCount}</span>
        <span>beschikbaar</span>
        <span style={{ color: 'var(--cream-dark)' }}>·</span>
        <span>{count} totaal</span>
        {geocoding && (
          <>
            <span style={{ color: 'var(--cream-dark)' }}>·</span>
            <span style={{ color: 'var(--green)' }}>Zoeken...</span>
          </>
        )}
      </div>
    </div>
  )
}

export default MapSearch
